import { makeAutoObservable, computed, runInAction } from 'mobx';
import type { Project } from './types';
import type { RootStore } from './RootStore';

export class ProjectStore {
  projects = new Map<string, Project>();
  private root: RootStore;

  constructor(root: RootStore) {
    this.root = root;
    makeAutoObservable(this, { all: computed });
  }

  get all(): Project[] {
    const result: Project[] = [];
    for (const p of this.projects.values()) {
      if (!p.isDeleted) result.push(p);
    }
    return result.sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1));
  }

  getById(id: string): Project | undefined {
    const p = this.projects.get(id);
    return p && !p.isDeleted ? p : undefined;
  }

  loadFromRecords(records: Record<string, unknown>[]): void {
    runInAction(() => {
      for (const r of records) this.projects.set(r.id as string, r as unknown as Project);
    });
  }

  applyChanges(changes: { id: string; data?: Record<string, unknown>; isDeleted: boolean }[]): void {
    runInAction(() => {
      for (const c of changes) {
        if (c.isDeleted) this.projects.delete(c.id);
        else if (c.data) this.projects.set(c.id, c.data as unknown as Project);
      }
    });
  }

  removeFromMap(id: string): void {
    this.projects.delete(id);
  }

  create(data: Partial<Project>): Project {
    const p: Project = {
      id: crypto.randomUUID(), name: data.name ?? '', description: data.description,
      contactId: data.contactId, ownerId: data.ownerId,
      orgId: '', syncVersion: 0, isDeleted: false,
      createdAt: new Date().toISOString(), updatedAt: new Date().toISOString(),
    };
    this.projects.set(p.id, p);
    this.root.syncStore.queueMutation('project', 'create', p.id, p);
    return p;
  }

  update(id: string, patch: Partial<Project>): void {
    const existing = this.projects.get(id);
    if (!existing) return;
    this.projects.set(id, { ...existing, ...patch, updatedAt: new Date().toISOString() });
    this.root.syncStore.queueMutation('project', 'update', id, patch);
  }

  delete(id: string): void {
    const existing = this.projects.get(id);
    if (existing) {
      this.projects.set(id, { ...existing, isDeleted: true, updatedAt: new Date().toISOString() });
    }
    this.root.syncStore.queueMutation('project', 'delete', id);
  }

  restore(id: string): void {
    const existing = this.projects.get(id);
    if (existing) {
      this.projects.set(id, { ...existing, isDeleted: false, updatedAt: new Date().toISOString() });
      this.root.syncStore.queueMutation('project', 'update', id, { isDeleted: false });
    }
  }

  clear(): void { this.projects.clear(); }
}
